import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { authApi } from '../services/api';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error, already
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyToken(token);
    } else {
      setStatus('error');
      setMessage('No verification token found.');
    }
  }, [searchParams]);

  const verifyToken = async (token) => {
    try {
      const response = await authApi.verifyEmail(token);
      if (response.data.message.includes('already')) {
        setStatus('already');
      } else {
        setStatus('success');
      }
      setMessage(response.data.message);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. The link may be expired or invalid.');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4" data-testid="verify-email-page">
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-[#005AA0] mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Verifying Your Email
            </h2>
            <p className="text-neutral-500">Please wait...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-[#2A7828]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-[#2A7828]" />
            </div>
            <h2 className="text-2xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Email Verified!
            </h2>
            <p className="text-neutral-600 mb-6">{message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/community-login" className="inline-flex items-center justify-center px-6 py-3 bg-[#005AA0] text-white rounded-full font-semibold hover:bg-[#004B87] transition-colors" data-testid="login-community-btn">
                Community Login
              </Link>
              <Link to="/business-login" className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#005AA0] text-[#005AA0] rounded-full font-semibold hover:bg-[#005AA0]/5 transition-colors" data-testid="login-business-btn">
                Business Login
              </Link>
            </div>
          </>
        )}

        {status === 'already' && (
          <>
            <div className="w-16 h-16 bg-[#005AA0]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-10 h-10 text-[#005AA0]" />
            </div>
            <h2 className="text-2xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Already Verified
            </h2>
            <p className="text-neutral-600 mb-6">{message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/community-login" className="inline-flex items-center justify-center px-6 py-3 bg-[#005AA0] text-white rounded-full font-semibold hover:bg-[#004B87] transition-colors">
                Community Login
              </Link>
              <Link to="/business-login" className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#005AA0] text-[#005AA0] rounded-full font-semibold hover:bg-[#005AA0]/5 transition-colors">
                Business Login
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Verification Failed
            </h2>
            <p className="text-neutral-600 mb-6">{message}</p>
            <Link to="/register" className="inline-flex items-center justify-center px-6 py-3 bg-[#005AA0] text-white rounded-full font-semibold hover:bg-[#004B87] transition-colors" data-testid="back-to-register-btn">
              Back to Register
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
